import React, { useState, useRef, useEffect } from 'react';
import { Search, UserPlus, Users2, X } from 'lucide-react';
import { api } from '../../lib/api';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  members: string[];
  permissions: any[];
  isSystemGroup?: boolean;
  groupType?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserGroupSelectorProps {
  selectedUsers: string[];
  selectedGroups: string[];
  onAddUser: (userId: string) => void;
  onAddGroup: (groupId: string) => void;
  onRemoveUser: (userId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  disabled?: boolean;
  hideSelected?: boolean; // Hide the selected users/groups display
}

export default function UserGroupSelector({
  selectedUsers,
  selectedGroups,
  onAddUser,
  onAddGroup,
  onRemoveUser,
  onRemoveGroup,
  disabled = false,
  hideSelected = false
}: UserGroupSelectorProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Load users and groups on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setUsersLoading(true);
        setGroupsLoading(true);

        const [usersResponse, groupsResponse] = await Promise.all([
          api.get('/groups/organization/users'),
          api.get('/groups')
        ]);

        setUsers(usersResponse.data.data || []);
        setGroups(groupsResponse.data.data || []);
      } catch (error) {
        console.error('Error loading users and groups:', error);
      } finally {
        setUsersLoading(false);
        setGroupsLoading(false);
      }
    };

    loadData();
  }, []);

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  const getGroupName = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown Group';
  };

  const getUser = (userId: string) => {
    return users.find(u => u.id === userId);
  };

  const getGroup = (groupId: string) => {
    return groups.find(g => g.id === groupId);
  };

  return (
    <div className="space-y-4">
      {/* Selected Users and Groups Display */}
      {!hideSelected && (
        <div className="space-y-2">
        {/* Selected Users */}
        {selectedUsers.map(userId => {
          const user = getUser(userId);
          return (
            <div key={userId} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
              <div className="flex items-center space-x-2">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                    className="h-6 w-6 rounded-full"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                    {user ? `${user.firstName[0]}${user.lastName[0]}` : 'U'}
                  </div>
                )}
                <div>
                  <span className="text-sm font-medium">{getUserName(userId)}</span>
                  {user && <span className="text-xs text-gray-500 ml-2">({user.email})</span>}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveUser(userId)}
                  className="text-red-500 hover:text-red-700 transition-colors p-1"
                  title="Remove user"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}

        {/* Selected Groups */}
        {selectedGroups.map(groupId => {
          const group = getGroup(groupId);
          const isSystemGroup = group?.isSystemGroup;
          return (
            <div key={groupId} className={`flex items-center justify-between rounded-lg p-2 ${
              isSystemGroup ? 'bg-green-50' : 'bg-blue-50'
            }`}>
              <div className="flex items-center space-x-2">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                  isSystemGroup ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  <Users2 className={`h-3 w-3 ${
                    isSystemGroup ? 'text-green-600' : 'text-blue-600'
                  }`} />
                </div>
                <div>
                  <span className={`text-sm font-medium ${
                    isSystemGroup ? 'text-green-800' : ''
                  }`}>
                    {getGroupName(groupId)}
                    {isSystemGroup && (
                      <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">
                        System
                      </span>
                    )}
                  </span>
                  {group?.description && (
                    <span className="text-xs text-gray-500 ml-2">({group.description})</span>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveGroup(groupId)}
                  className="text-red-500 hover:text-red-700 transition-colors p-1"
                  title="Remove group"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}

        {selectedUsers.length === 0 && selectedGroups.length === 0 && (
          <div className="text-gray-500 text-sm text-center py-4">
            No users or groups selected
          </div>
        )}
        </div>
      )}

      {/* Add Users and Groups Section */}
      {!disabled && (
        <div className="space-y-3 border-t pt-3">
          <AddUserSection
            users={users}
            selectedUsers={selectedUsers}
            onAddUser={onAddUser}
            usersLoading={usersLoading}
          />
          
          <AddGroupSection
            groups={groups}
            selectedGroups={selectedGroups}
            onAddGroup={onAddGroup}
            groupsLoading={groupsLoading}
          />
        </div>
      )}
    </div>
  );
}

// Component for adding users
function AddUserSection({ 
  users, 
  selectedUsers, 
  onAddUser, 
  usersLoading 
}: {
  users: User[];
  selectedUsers: string[];
  onAddUser: (userId: string) => void;
  usersLoading: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Filter users who are not already selected
  const availableUsers = users.filter(user => 
    !selectedUsers.includes(user.id) &&
    (user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 300;
      
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAddUser = (userId: string) => {
    onAddUser(userId);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors"
      >
        <UserPlus className="h-4 w-4" />
        <span>Add user</span>
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 z-10 bg-white border border-gray-300 rounded-lg shadow-lg ${
          dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
        }`}>
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Users List */}
          <div className="max-h-48 overflow-y-auto">
            {usersLoading ? (
              <div className="p-3 text-sm text-gray-500">Loading users...</div>
            ) : availableUsers.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                {searchTerm ? 'No users found matching your search' : 'All users are already selected'}
              </div>
            ) : (
              availableUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleAddUser(user.id)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                      {user.firstName[0]}{user.lastName[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Component for adding groups
function AddGroupSection({ 
  groups, 
  selectedGroups, 
  onAddGroup, 
  groupsLoading
}: {
  groups: Group[];
  selectedGroups: string[];
  onAddGroup: (groupId: string) => void;
  groupsLoading: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Filter groups that are not already selected and match search
  const availableGroups = groups.filter(group => 
    !selectedGroups.includes(group.id) &&
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 300;
      
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAddGroup = (groupId: string) => {
    onAddGroup(groupId);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
      >
        <Users2 className="h-4 w-4" />
        <span>Add group</span>
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 z-10 bg-white border border-gray-300 rounded-lg shadow-lg ${
          dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
        }`}>
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Groups List */}
          <div className="max-h-48 overflow-y-auto">
            {groupsLoading ? (
              <div className="p-3 text-sm text-gray-500">Loading groups...</div>
            ) : availableGroups.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                {searchTerm ? 'No groups found matching your search' : 'No groups available to add'}
              </div>
            ) : (
              availableGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => handleAddGroup(group.id)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
                >
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center ${
                    group.isSystemGroup ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    <Users2 className={`h-3 w-3 ${
                      group.isSystemGroup ? 'text-green-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className={`font-medium ${
                      group.isSystemGroup ? 'text-green-800' : ''
                    }`}>
                      {group.name}
                      {group.isSystemGroup && (
                        <span className="ml-1 text-xs bg-green-100 text-green-700 px-1 rounded">
                          System
                        </span>
                      )}
                    </div>
                    {group.description && (
                      <div className="text-xs text-gray-500">{group.description}</div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
